import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Row,
  UncontrolledAlert,
} from 'reactstrap';
import { UnitPcvFinantial, UnitPcvFinantialForm } from '../models/unitPcv';
import useManageUnitPcvFinantialForm from '../hooks/useManageUnitPcvFinantialForm';

interface Props {
  finantial?: UnitPcvFinantial;
  handleForm: (form: UnitPcvFinantialForm, errorRepository: Function) => void;
}

const ManageUnitPcvFinantialForm = ({ finantial, handleForm }: Props) => {
  const { handleSubmit, errors, control, isLoading, error, isEditing, setError } =
    useManageUnitPcvFinantialForm(finantial);

  const internalHandleForm = useCallback(
    (form: UnitPcvFinantialForm) => handleForm(form, setError),
    [],
  );

  return (
    <Form role="form" onSubmit={handleSubmit(internalHandleForm)}>
      {error && (
        <UncontrolledAlert color="danger" fade={false}>
          <span className="alert-inner--text">{error}</span>
        </UncontrolledAlert>
      )}

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="initial_reserve">
              Reserva inicial
            </label>
            <Controller
              name="initial_reserve"
              control={control}
              defaultValue={finantial?.initial_reserve}
              key={`${finantial?.initial_reserve}-initial_reserve`}
              render={({ field }) => (
                <Input
                  className={errors.initial_reserve?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.initial_reserve?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="current_reserve">
              Reserva actual
            </label>
            <Controller
              name="current_reserve"
              control={control}
              defaultValue={finantial?.current_reserve}
              key={`${finantial?.current_reserve}-current_reserve`}
              render={({ field }) => (
                <Input
                  className={errors.current_reserve?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.current_reserve?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="initial_down_payment">
              Prima inicial
            </label>
            <Controller
              name="initial_down_payment"
              control={control}
              defaultValue={finantial?.initial_down_payment}
              key={`${finantial?.initial_down_payment}-initial_down_payment`}
              render={({ field }) => (
                <Input
                  className={errors.initial_down_payment?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.initial_down_payment?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="down_payment_paid">
              Prima pagada
            </label>
            <Controller
              name="down_payment_paid"
              control={control}
              defaultValue={finantial?.down_payment_paid}
              key={`${finantial?.down_payment_paid}-down_payment_paid`}
              render={({ field }) => (
                <Input
                  className={errors.down_payment_paid?.message && 'is-invalid'}
                  {...field}
                  type="number"
                  step="any"
                />
              )}
            />
            <h5 className="text-danger">{errors.down_payment_paid?.message}</h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="percentage_down_payment">
              Porcentaje de prima (%)
            </label>
            <Controller
              name="percentage_down_payment"
              control={control}
              defaultValue={finantial?.percentage_down_payment}
              key={`${finantial?.percentage_down_payment}-percentage_down_payment`}
              render={({ field }) => (
                <Input
                  className={
                    errors.percentage_down_payment?.message && 'is-invalid'
                  }
                  {...field}
                  type="number"
                  step="any"
                  min={0}
                  max={100}
                />
              )}
            />
            <h5 className="text-danger">
              {errors.percentage_down_payment?.message}
            </h5>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <label className="form-control-label" htmlFor="quotas_down_payment">
              Cuotas de prima
            </label>
            <Controller
              name="quotas_down_payment"
              control={control}
              defaultValue={finantial?.quotas_down_payment ?? 0}
              key={`${finantial?.quotas_down_payment}-quotas_down_payment`}
              render={({ field }) => (
                <Input
                  className={errors.quotas_down_payment?.message && 'is-invalid'}
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">{errors.quotas_down_payment?.message}</h5>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <label
              className="form-control-label"
              htmlFor="quotas_down_payment_paids"
            >
              Cuotas de prima pagadas
            </label>
            <Controller
              name="quotas_down_payment_paids"
              control={control}
              defaultValue={finantial?.quotas_down_payment_paids ?? 0}
              key={`${finantial?.quotas_down_payment_paids}-quotas_down_payment_paids`}
              render={({ field }) => (
                <Input
                  className={
                    errors.quotas_down_payment_paids?.message && 'is-invalid'
                  }
                  {...field}
                  type="number"
                />
              )}
            />
            <h5 className="text-danger">
              {errors.quotas_down_payment_paids?.message}
            </h5>
          </FormGroup>
        </Col>
      </Row>

      <Button color="primary" disabled={isLoading}>
        {isEditing ? 'Actualizar' : 'Crear'}
      </Button>
    </Form>
  );
};

export default ManageUnitPcvFinantialForm;
